import React from "react";
import {useSetStyleRegistory} from "../../hooks/useSetStyleRegistory";
import {FunnelKey, RoleKey, RoleKeyPair} from "../../models/Keys";
import {VisibleRoleCell} from "../../models/RoleCell";
import {InlineEditor} from "./InlineEditor";
import {KeyboardEventUtil} from "../../utils/KeyboardEventUtil";
import {RoleLines} from "../../models/RoleLines";
import {RoleHeaders} from "../../models/RoleHeader";
import {FunnelHeaders} from "../../models/Funnel";
import {fireCopyEvent} from "../../utils/browser";

export type RCRoleCellsProps = {
    role_lines: RoleLines;
    role_headers: RoleHeaders;
    funnel_headers: FunnelHeaders;
    readOnly: boolean;
    onSelectCell: (role_key_pair: RoleKeyPair) => void;
    onCellNameChange: (role_key: RoleKey, funnel_keys: FunnelKey[], name: string) => void;
    onJoinCell: (role_cell: VisibleRoleCell) => void;
    onSplitCell: (role_cell: VisibleRoleCell) => void;
};
export const RCRoleCells = (props: RCRoleCellsProps) => {

    return (
        <div style={
            {
                display: "grid",
                gridTemplateRows: props.role_headers.GridTemplateRows,
                gap: 10,
            }
        }>
            {props.role_lines.mapLines(role_line =>
                <div key={role_line.header.Key} style={
                    {
                        display: "grid",
                        gridTemplateColumns: props.funnel_headers.GridTemplateColumns,
                        gap: 10,
                    }
                }>
                    {role_line.mapCells(cell =>
                        <RCRoleCell key={cell.KeyPair.toString()}
                                    role_cell={cell}
                                    readOnly={props.readOnly}
                                    onSelectCell={props.onSelectCell}
                                    onCellNameChange={props.onCellNameChange}
                                    onJoinCell={props.onJoinCell}
                                    onSplitCell={props.onSplitCell}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
type RoleCellProps = {
    role_cell: VisibleRoleCell;
    readOnly: boolean;
    onSelectCell: (role_key_pair: RoleKeyPair) => void;
    onCellNameChange: (role_key: RoleKey, funnel_keys: FunnelKey[], new_name: string) => void;
    onJoinCell: (role_cell: VisibleRoleCell) => void;
    onSplitCell: (role_cell: VisibleRoleCell) => void;
}
const RCRoleCell = (props: RoleCellProps) => {

    const [isEdit, setIsEdit] = React.useState<boolean>(false);
    const [show_collaborator_label, setShowCollaboratorLabel] = React.useState(false);
    const ref = React.useRef<HTMLDivElement>(null);
    const setStyleRegistry = useSetStyleRegistory();

    React.useEffect(() => {
        setStyleRegistry.add(props.role_cell.KeyPair.LineKey, ref);
        setStyleRegistry.add(props.role_cell.KeyPair.FunnelKey, ref);
        return () => {
            setStyleRegistry.remove(props.role_cell.KeyPair.LineKey, ref);
            setStyleRegistry.remove(props.role_cell.KeyPair.FunnelKey, ref);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const style = {
        gridColumn: props.role_cell.State.GridColumn,
        backgroundColor: props.role_cell.State.BackgroundColor,
        '--background-color': props.role_cell.State.BackgroundColor,
    };

    const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
        if (props.role_cell.State.IsSelected) {
            !props.readOnly && setIsEdit(true);
        } else {
            props.onSelectCell(props.role_cell.KeyPair);
        }

        e.stopPropagation(); // inhibit clearSelect
    }

    const handleKeyDown: React.KeyboardEventHandler = (e) => {
        if (KeyboardEventUtil.isStartEditKey(e.nativeEvent)) {
            !props.readOnly && setIsEdit(true);
            e.preventDefault();
        } else if (e.code === 'KeyJ') {
            !props.readOnly && props.onJoinCell(props.role_cell);
            e.preventDefault();
        } else if (e.code === 'KeyS') {
            !props.readOnly && props.role_cell && props.onSplitCell(props.role_cell);
            e.preventDefault();
        }

        if (KeyboardEventUtil.isCopyKey(e.nativeEvent)) {
            if (ref.current) {
                fireCopyEvent(ref.current);
            }
            e.preventDefault();
        }
    }

    const handleCopy: React.ClipboardEventHandler = (e) => {
        e.clipboardData.setData('text/plain', props.role_cell.OstensibleName);
        e.preventDefault();
    }

    React.useEffect(() => {
        if (props.role_cell.State.IsSelected) {
            ref.current?.focus();
        }
    }, [props.role_cell.State.IsSelected]);

    return (
        <div className="role_cell"
             ref={ref}
             data-is-selected={props.role_cell.State.IsSelected}
             data-collaborator-selected={0 < props.role_cell.State.Collaborators.length}
             onClick={handleClick}
             onKeyDown={handleKeyDown}
             onCopy={handleCopy}
             onMouseEnter={(e) => setShowCollaboratorLabel(true)}
             onMouseLeave={(e) => setShowCollaboratorLabel(false)}
             tabIndex={0} /* hack for capture keyboard event */
             style={style}>
            <InlineEditor value={props.role_cell.OstensibleName} isEdit={isEdit}
                          onChangeValue={value => {
                              if (props.role_cell.isChangeableName(value)) {
                                  props.onCellNameChange(props.role_cell.KeyPair.LineKey, props.role_cell.State.JoinedKeys, value);
                              }
                              ref.current?.focus();
                          }}
                          onLeave={() => {
                              setIsEdit(false);
                          }}
            />
            <div className="collaborator_label" style={{display: show_collaborator_label ? 'block': 'none'}}>
                {props.role_cell.State.Collaborators.join(", ")}
            </div>
        </div>
    )
}
